import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { generateProcessGlobalPdf } from '@/lib/processGlobalPdf';
import { normalizeMiniIpipLevelForBenchmark } from '@/lib/affinityInterpretation';
import { getExpertBenchmark, type RoleType } from '@/config/tests';
import type { Attempt, Process, Result } from '@/types';

type AttemptWithMeta = Attempt & {
  candidateName?: string;
  processTitle?: string;
  globalMatch?: number;
};

function getMatchColor(score: number): string {
  if (score >= 80) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
  if (score >= 60) return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
  return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400';
}

function getMatchLabel(score: number): string {
  if (score >= 80) return 'Alineación Alta';
  if (score >= 60) return 'Alineación Media';
  return 'Brecha Amplia';
}

export function ResultsPage() {
  const { appUser, isOwner } = useAuth();
  const [attempts, setAttempts] = useState<AttemptWithMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [queryError, setQueryError] = useState<string | null>(null);
  /** Intentos totales devueltos por la consulta (antes de filtrar por completados). */
  const [totalFromQuery, setTotalFromQuery] = useState<number | null>(null);

  useEffect(() => {
    if (!appUser) return;
    if (!isOwner && (appUser.companyId == null || appUser.companyId === '')) {
      setAttempts([]);
      setTotalFromQuery(null);
      setLoading(false);
      setQueryError('No tienes empresa asignada. No se pueden cargar resultados.');
      return;
    }
    setQueryError(null);
    setTotalFromQuery(null);
    const load = async () => {
      try {
        let snap: Awaited<ReturnType<typeof getDocs>>;
        if (isOwner) {
          // Sin orderBy en query: completedAt puede no existir (in_progress) y Firestore excluiría esos docs
          snap = await getDocs(collection(db, 'attempts'));
        } else {
          const companyIdStr = (appUser.companyId ?? '').trim();
          // Solo where; sin orderBy para no excluir docs sin completedAt (Gemini: orderBy excluye los que no tienen el campo)
          const qStr = query(
            collection(db, 'attempts'),
            where('companyId', '==', companyIdStr)
          );
          snap = await getDocs(qStr);
        }
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          startedAt: d.data().startedAt ?? '',
          completedAt: d.data().completedAt,
          candidateName: undefined,
          processTitle: undefined,
          globalMatch: undefined,
        })) as AttemptWithMeta[];

        // Fetch processes para mapear processId -> title y jobProfile
        const processTitles: Record<string, string> = {};
        const processesData: Record<string, Process> = {};
        try {
          let procSnap: Awaited<ReturnType<typeof getDocs>>;
          if (isOwner) {
            procSnap = await getDocs(collection(db, 'processes'));
          } else {
            const companyIdStr = (appUser.companyId ?? '').trim();
            procSnap = await getDocs(
              query(collection(db, 'processes'), where('companyId', '==', companyIdStr))
            );
          }
          procSnap.docs.forEach((d) => {
            const data = d.data();
            processTitles[d.id] = (data?.title as string)?.trim() || 'Proceso sin título';
            processesData[d.id] = { id: d.id, ...data } as Process;
          });
        } catch (e) {
          // Error cargando procesos: se ignora, processTitles quedará vacío
        }

        // Excluir intentos de procesos eliminados (no presentes en processTitles)
        const filteredList = list.filter((a) => a.processId && processTitles[a.processId] !== undefined);

        for (const a of filteredList) {
          // Asignar processTitle (ya sabemos que existe en processTitles)
          const item = filteredList.find((x) => x.id === a.id);
          if (item) {
            item.processTitle = processTitles[a.processId] ?? 'Proceso Desconocido';
          }

          // Para completados: fetch results y calcular globalMatch
          if (a.status === 'completed') {
            try {
              const resSnap = await getDoc(doc(db, 'results', a.id));
              if (resSnap.exists()) {
                const data = resSnap.data();
                const scores = (data?.scores ?? []) as Result['scores'];
                const processData = processesData[a.processId];
                const jobProfile =
                  processData?.jobProfile ??
                  (processData?.roleType ? getExpertBenchmark(processData.roleType as RoleType) : null);

                if (!jobProfile) {
                  const validScores = scores
                    .map((s) => s.scorePercent ?? s.scaleScore)
                    .filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
                  const avg =
                    validScores.length > 0
                      ? validScores.reduce((sum, v) => sum + v, 0) / validScores.length
                      : 0;
                  const target = filteredList.find((x) => x.id === a.id);
                  if (target) target.globalMatch = Math.round(avg);
                } else {
                  let totalWeight = 0;
                  let weightedSum = 0;

                  scores.forEach((s) => {
                    const bench = jobProfile[s.testId];
                    const pct = s.scorePercent ?? (typeof s.scaleScore === 'number' ? s.scaleScore : undefined);
                    if (bench && bench.minScore !== undefined && bench.weight && typeof pct === 'number' && bench.weight > 0) {
                      let match = 100;
                      if (pct < bench.minScore) {
                        match = Math.max(0, 100 - (bench.minScore - pct));
                      }
                      weightedSum += match * bench.weight;
                      totalWeight += bench.weight;
                    }
                  });

                  const ipipBench = jobProfile['MINI_IPIP_20'];
                  const ipipData = data?.moduleAggregates?.MINI_IPIP_20 as { dimensions?: Record<string, { level?: string }> } | undefined;
                  if (ipipBench?.dimensions && ipipData?.dimensions) {
                    Object.entries(ipipBench.dimensions).forEach(([dimKey, dimConf]) => {
                      if (dimConf.weight > 0 && dimConf.expectedLevel !== 'Indiferente') {
                        const candLevelRaw = ipipData.dimensions?.[dimKey]?.level;
                        // Misma lógica que computeAffinityInterpretation: resultados usan Bajo/Promedio/Alto; benchmark usa En desarrollo/Adecuado/Alto.
                        const candLevel =
                          normalizeMiniIpipLevelForBenchmark(candLevelRaw) ?? candLevelRaw;
                        let dimMatch = 0;

                        if (candLevel === dimConf.expectedLevel) {
                          dimMatch = 100;
                        } else if (
                          (dimConf.expectedLevel === 'Alto' && candLevel === 'Adecuado') ||
                          (dimConf.expectedLevel === 'En desarrollo' && candLevel === 'Adecuado') ||
                          (dimConf.expectedLevel === 'Adecuado' && (candLevel === 'Alto' || candLevel === 'En desarrollo'))
                        ) {
                          dimMatch = 50;
                        }

                        weightedSum += dimMatch * dimConf.weight;
                        totalWeight += dimConf.weight;
                      }
                    });
                  }

                  const target = filteredList.find((x) => x.id === a.id);
                  if (target) target.globalMatch = totalWeight > 0 ? Math.round(weightedSum / totalWeight) : undefined;
                }
              }
            } catch {
              // Ignorar errores al cargar un resultado individual
            }
          }
        }

        for (const a of filteredList) {
          const rawCid = a.candidateId;
          const cid = typeof rawCid === 'string' ? rawCid : (rawCid as { id?: string } | null)?.id;
          if (cid) {
            try {
              const cSnap = await getDoc(doc(db, 'candidates', cid));
              if (cSnap.exists()) {
                const data = cSnap.data();
                const name =
                  (data?.fullName as string)?.trim() ||
                  (data?.full_name as string)?.trim() ||
                  (data?.email as string)?.trim() ||
                  (data?.displayName as string)?.trim() ||
                  'Candidato';
                const item = filteredList.find((x) => x.id === a.id);
                if (item) item.candidateName = name;
              } else {
                const item = filteredList.find((x) => x.id === a.id);
                if (item) item.candidateName = `Candidato (${cid.slice(0, 8)}…)`;
              }
            } catch {
              const item = filteredList.find((x) => x.id === a.id);
              if (item) item.candidateName = `Candidato (${cid.slice(0, 8)}…)`;
            }
          }
        }
        // Ordenar en cliente: más reciente primero (completedAt o startedAt); así no excluimos docs sin completedAt
        filteredList.sort((a, b) => {
          const aAt = a.completedAt || a.startedAt || '';
          const bAt = b.completedAt || b.startedAt || '';
          return bAt.localeCompare(aAt);
        });
        setTotalFromQuery(list.length);
        setAttempts(filteredList);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        const code = err && typeof err === 'object' && 'code' in err ? (err as { code?: string }).code : '';
        if (code === 'permission-denied' || /permission|denied/i.test(msg)) {
          setQueryError('No tienes permiso para ver los resultados. Comprueba las reglas de Firestore.');
        } else if (code === 'failed-precondition' || /index|indexes/i.test(msg)) {
          setQueryError(
            'La consulta requiere un índice en Firestore. Crea el índice desde el enlace que aparece en la consola del navegador (F12 → Console) y espera 2–5 minutos hasta que esté "Habilitado". Luego recarga esta página.'
          );
        } else {
          setQueryError(msg || 'Error al cargar resultados.');
        }
        setAttempts([]);
        setTotalFromQuery(null);
      }
      setLoading(false);
    };
    load();
  }, [appUser, isOwner]);

  const [collapsedProcessIds, setCollapsedProcessIds] = useState<Set<string>>(new Set());

  const groupedAttempts = useMemo(() => {
    const byProcess = new Map<string, AttemptWithMeta[]>();
    for (const a of attempts) {
      const pid = a.processId || '_sin_proceso';
      if (!byProcess.has(pid)) byProcess.set(pid, []);
      byProcess.get(pid)!.push(a);
    }
    for (const arr of byProcess.values()) {
      arr.sort((a, b) => {
        const aDone = a.status === 'completed' ? 1 : 0;
        const bDone = b.status === 'completed' ? 1 : 0;
        if (bDone !== aDone) return bDone - aDone;
        const aMatch = a.globalMatch ?? -1;
        const bMatch = b.globalMatch ?? -1;
        return bMatch - aMatch;
      });
    }
    return byProcess;
  }, [attempts]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Resultados</h1>
      {queryError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {queryError}
        </div>
      )}
      {loading && <p className="text-muted-foreground">Cargando...</p>}
      {!loading && !queryError && attempts.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {totalFromQuery === 0
              ? 'No se encontró ningún intento. Si eres admin, comprueba que los attempts en Firestore tengan el mismo companyId que tu usuario.'
              : 'No hay evaluaciones completadas aún.'}
          </CardContent>
        </Card>
      )}
      {!loading && !queryError && attempts.length > 0 && (
        <div className="space-y-6">
          {Array.from(groupedAttempts.entries()).map(([processId, groupAttempts]) => {
            const processTitle = groupAttempts[0]?.processTitle ?? 'Proceso Desconocido';
            const isCollapsed = collapsedProcessIds.has(processId);
            const toggle = () => {
              setCollapsedProcessIds((prev) => {
                const next = new Set(prev);
                if (next.has(processId)) next.delete(processId);
                else next.add(processId);
                return next;
              });
            };
            return (
              <Card key={processId}>
                <CardHeader
                  className="cursor-pointer select-none hover:bg-muted/50 transition-colors rounded-t-lg"
                  onClick={toggle}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
                >
                  <div className="flex items-center justify-between gap-4">
                    <CardTitle className="text-base">{processTitle}</CardTitle>
                    <span className="text-muted-foreground text-sm">
                      {groupAttempts.length} candidato{groupAttempts.length !== 1 ? 's' : ''}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const companyId = groupAttempts[0]?.companyId;
                          let companyName = 'Empresa';
                          if (companyId) {
                            const companySnap = await getDoc(doc(db, 'companies', companyId));
                            if (companySnap.exists()) {
                              companyName = (companySnap.data()?.name as string)?.trim() || companyName;
                            }
                          }
                          const generatedBy = appUser?.displayName || appUser?.email || 'Usuario';
                          await generateProcessGlobalPdf(processTitle, groupAttempts, companyName, generatedBy);
                        } catch (err) {
                          console.error('Error generando PDF:', err);
                          alert('Error al generar el PDF. Permite ventanas emergentes si es necesario.');
                        }
                      }}
                    >
                      Descargar Reporte Global
                    </Button>
                    <span className="text-muted-foreground shrink-0" aria-hidden>
                      {isCollapsed ? '▸' : '▾'}
                    </span>
                  </div>
                </CardHeader>
                {!isCollapsed && (
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="pb-2 pr-4 font-medium">Candidato</th>
                          <th className="pb-2 pr-4 font-medium">Estado</th>
                          <th className="pb-2 pr-4 font-medium">Fecha</th>
                          <th className="pb-2 pr-4 font-medium">Afinidad</th>
                          <th className="pb-2 font-medium">Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupAttempts.map((a) => (
                          <tr key={a.id} className="border-b py-2 last:border-0">
                            <td className="py-2 pr-4 font-medium">
                              {a.candidateName ?? (typeof a.candidateId === 'string' ? a.candidateId : 'Candidato')}
                            </td>
                            <td className="py-2 pr-4">
                              <span
                                className={`rounded px-2 py-0.5 text-xs font-medium ${
                                  a.status === 'completed'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                                }`}
                              >
                                {a.status === 'completed' ? 'Completado' : 'En curso'}
                              </span>
                            </td>
                            <td className="py-2 pr-4 text-muted-foreground">
                              {a.completedAt
                                ? new Date(a.completedAt).toLocaleDateString()
                                : new Date(a.startedAt).toLocaleDateString()}
                            </td>
                            <td className="py-2 pr-4">
                              {a.status === 'completed' && a.globalMatch != null ? (
                                <span
                                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getMatchColor(a.globalMatch)}`}
                                >
                                  {getMatchLabel(a.globalMatch)} ({a.globalMatch}%)
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="py-2">
                              <span className="flex gap-2">
                                <Link to={`/app/results/${a.id}`} className="text-primary hover:underline">
                                  Ver resultado
                                </Link>
                                {a.status === 'completed' && (
                                  <Link to={`/app/results/${a.id}/pdf`} className="text-primary hover:underline">
                                    PDF
                                  </Link>
                                )}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
